/**
 * A QUIÉN LLAMAR SI ESTA PERSONA SE CAE EN LA CANCHA.
 *
 * ES LA MITAD QUE SE LEE del bloque que se llena en el perfil propio
 * (`EmergencySection`). Javier: «si alguien ve el perfil los contactos de
 * emergencia deben de aparecer», y la razón es la misma por la que el dato
 * existe: quien está al lado de alguien tirado en la cancha casi nunca es de
 * su club, y un dato que solo ve el dueño no sirve el día que hace falta.
 *
 * SI NO HAY NADA, NO SE DIBUJA NADA. Ni el título ni un «no cargó sus datos»:
 * quien mira el perfil de otro no puede hacer nada con ese reproche, y una
 * sección vacía en cada perfil es ruido en todos para avisar de ninguno.
 *
 * LOS TELÉFONOS SON ENLACES `tel:`. Es todo el punto: quien abre esto está
 * buscando marcar, no leer. Un número que hay que copiar a mano es un número
 * que se marca mal con el pulso acelerado.
 */
import { useTranslation } from 'react-i18next';
import type { EmergencyInfo } from '@tennisup/contracts';

export function EmergencyInfoCard({ emergency }: { emergency: EmergencyInfo | undefined }): JSX.Element | null {
  const { t } = useTranslation();

  // `EmergencyInfo` es una promesa sobre el contrato, no sobre el cable: un
  // servidor una versión atrás no manda este campo. Ver `EmergencySection`,
  // donde el mismo descuido dejaba el perfil ENTERO en blanco.
  const datos = emergency ?? { bloodType: null, medicalNotes: null, contacts: [] };
  const contactos = Array.isArray(datos.contacts) ? datos.contacts : [];

  const hayAlgo =
    datos.bloodType !== null || datos.medicalNotes !== null || contactos.length > 0;
  if (!hayAlgo) return null;

  return (
    <section className="emergency-card">
      <h2>{t('player.emergencyTitle')}</h2>

      {datos.bloodType !== null && (
        <p className="emergency-blood">
          {t('profile.emergencyBloodType')}: <strong>{datos.bloodType}</strong>
        </p>
      )}

      {datos.medicalNotes !== null && (
        /* `pre-line` para que los saltos de línea que la persona escribió se
           vean: lo médico se llena en renglones —una condición por línea— y
           aplastarlo en un párrafo lo vuelve ilegible justo cuando importa. */
        <p className="emergency-notes">{datos.medicalNotes}</p>
      )}

      {contactos.length > 0 && (
        <ul className="emergency-contacts">
          {contactos.map((contacto) => (
            <li key={contacto.id}>
              <span className="emergency-who">
                {contacto.name}
                {contacto.relationship !== null && (
                  <span className="help"> · {contacto.relationship}</span>
                )}
              </span>
              {/* El número tal como lo escribieron: `tel:` aguanta guiones y
                  espacios, y mostrarlo distinto de como está anotado haría
                  dudar de si es el mismo. */}
              <a className="emergency-phone" href={`tel:${contacto.phone}`}>
                {contacto.phone}
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
